'use client';

import React, { useState } from 'react';
import Image, { StaticImageData } from 'next/image';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';

import { motion } from 'framer-motion';
import { FiMaximize2, FiImage } from 'react-icons/fi';

interface LightboxGalleryProps {
  images: { 
    src: string | StaticImageData; 
    title?: string; 
    width?: number; 
    height?: number 
  }[];
}

export default function LightboxGallery({ images }: LightboxGalleryProps) {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  const slides = images.map(({ src, title }) => ({
    src: typeof src === 'string' ? src : (src as StaticImageData).src,
    title,
  }));

  return (
    <div className="my-12 space-y-6">
      {/* Gallery Metadata Header */}
      <div className="flex items-center justify-between px-2">
        <div className="flex items-center gap-3">
          <FiImage className="text-indigo-500" />
          <span className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500">
            Attached_Visual_Assets: {images.length.toString().padStart(2, '0')}
          </span>
        </div>
        <div className="h-[1px] flex-1 mx-6 bg-zinc-200 dark:bg-zinc-800 hidden sm:block" />
        <span className="text-[9px] font-mono text-zinc-400 hidden sm:block">STATUS: VERIFIED</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {images.map(({ src, title, width, height }, i) => {
          const srcStr = typeof src === 'string' ? src : (src as StaticImageData).src;
          const aspectRatio = width && height ? height / width : 9 / 16;

          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              viewport={{ once: true }}
              className="group relative w-full rounded-2xl border border-zinc-200 dark:border-zinc-800 overflow-hidden cursor-zoom-in bg-zinc-100 dark:bg-zinc-900 shadow-sm hover:shadow-xl hover:shadow-indigo-500/10 transition-all duration-500"
              style={{ paddingTop: `${aspectRatio * 100}%` }}
              onClick={() => {
                setIndex(i);
                setOpen(true);
              }}
            >
              <Image
                src={srcStr}
                alt={title || `Gallery Asset ${i + 1}`}
                fill
                className="object-cover transition-transform duration-700 group-hover:scale-105"
                sizes="(max-width: 768px) 100vw, 33vw"
              />

              {/* Tactical Glass Overlay */}
              <div className="absolute inset-0 bg-indigo-600/0 group-hover:bg-indigo-600/5 transition-colors duration-300" />
              
              <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                <div className="p-2 bg-white/10 backdrop-blur-md rounded-lg border border-white/20 text-white shadow-xl">
                  <FiMaximize2 size={14} />
                </div>
              </div>

              {/* Bottom Label Section */}
              <div className="absolute bottom-0 inset-x-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300 bg-gradient-to-t from-black/80 to-transparent">
                 <p className="text-[10px] font-black text-white uppercase tracking-wider truncate">
                    {title || `NODE_0${i + 1}`}
                 </p>
              </div>
            </motion.div>
          );
        })}
      </div>

      <Lightbox
        open={open}
        close={() => setOpen(false)}
        slides={slides}
        index={index}
        plugins={[Zoom, Thumbnails]}
        on={{ view: ({ index }) => setIndex(index) }}
        styles={{
          container: { backgroundColor: "rgba(0, 0, 0, 0.98)" },
          thumbnailsContainer: { backgroundColor: "rgba(24, 24, 27, 0.8)" },
          thumbnail: { borderRadius: "8px", border: "1px solid rgba(255,255,255,0.1)" }
        }}
      />
    </div>
  );
}